import { useEffect, useState } from 'react';
import {
  Popover,
  Stack,
  Group,
  Text,
  Badge,
  Button,
  ScrollArea,
  Divider,
  Alert,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconRefresh,
  IconTrash,
  IconExternalLink,
  IconInfoCircle,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { getSyncQueue, type PendingSyncItem } from '../lib/syncQueue';

function reportRoute(item: PendingSyncItem): string {
  return item.formType === 'ONSITE_DELIVERY'
    ? `/delivery-report/${item.submissionId}`
    : `/service-report/${item.submissionId}`;
}

function formTypeLabel(formType: string): string {
  return formType === 'ONSITE_DELIVERY' ? 'Lieferschein' : 'Servicebericht';
}

/**
 * Details zur Sync-Warteschlange — was hängt, warum, und was man dagegen tun kann.
 *
 * Vorher zeigte die Kopfzeile nur „Sync ausstehend · N" ohne jede weitere Auskunft.
 * Besonders tückisch: Einträge, die ihre Wiederholungen aufgebraucht haben, werden
 * vom automatischen Sync dauerhaft übersprungen — sie blieben für immer stehen,
 * auch wenn die Ursache längst behoben war. Deshalb hier ein explizites
 * „Erneut versuchen" (setzt den Zähler zurück) und „Verwerfen".
 */
export function SyncQueuePopover({
  children,
  onManualSync,
}: {
  children: React.ReactNode;
  onManualSync?: () => void;
}) {
  const [opened, setOpened] = useState(false);
  const [items, setItems] = useState<PendingSyncItem[]>([]);
  const navigate = useNavigate();
  const queue = getSyncQueue();
  const maxRetries = queue.getMaxRetries();

  useEffect(() => {
    if (!opened) return;
    const refresh = () => setItems(queue.getPending());
    refresh();
    const interval = setInterval(refresh, 1500);
    return () => clearInterval(interval);
  }, [opened, queue]);

  const retryAll = () => {
    for (const item of queue.getPending()) {
      if (!item.conflicted) queue.resetRetries(item.submissionId);
    }
    setItems(queue.getPending());
    onManualSync?.();
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      width={420}
      shadow="md"
      withArrow
    >
      <Popover.Target>
        <div onClick={() => setOpened((v) => !v)} style={{ cursor: 'pointer' }}>
          {children}
        </div>
      </Popover.Target>

      <Popover.Dropdown>
        <Stack gap="sm">
          <Group justify="space-between">
            <Text fw={600} size="sm">
              Nicht übertragene Berichte
            </Text>
            <Badge variant="light">{items.length}</Badge>
          </Group>

          <Text size="xs" c="dimmed">
            Diese Entwürfe liegen lokal auf diesem Gerät und sind noch nicht auf dem
            Server. Sie gehen nicht verloren.
          </Text>

          {items.length === 0 ? (
            <Alert color="green" variant="light" icon={<IconInfoCircle size={16} />}>
              <Text size="sm">Alles übertragen.</Text>
            </Alert>
          ) : (
            <ScrollArea.Autosize mah={280}>
              <Stack gap="xs">
                {items.map((item) => {
                  const exhausted = item.retryCount >= maxRetries;
                  return (
                    <div key={item.submissionId}>
                      <Group justify="space-between" wrap="nowrap" align="flex-start">
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <Group gap={6}>
                            <Text size="sm" fw={500}>
                              {formTypeLabel(item.formType)}
                            </Text>
                            {item.conflicted ? (
                              <Badge size="xs" color="orange" variant="light">
                                Konflikt
                              </Badge>
                            ) : exhausted ? (
                              <Badge size="xs" color="red" variant="light">
                                gestoppt nach {item.retryCount} Versuchen
                              </Badge>
                            ) : (
                              <Badge size="xs" color="blue" variant="light">
                                wartet
                              </Badge>
                            )}
                          </Group>
                          {item.errorMessage && (
                            <Text size="xs" c="dimmed" lineClamp={2}>
                              {item.errorMessage}
                            </Text>
                          )}
                        </div>
                        <Group gap={4} wrap="nowrap">
                          <Tooltip label="Bericht öffnen">
                            <ActionIcon
                              variant="subtle"
                              size="sm"
                              onClick={() => {
                                setOpened(false);
                                navigate(reportRoute(item));
                              }}
                            >
                              <IconExternalLink size={14} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label="Erneut versuchen">
                            <ActionIcon
                              variant="subtle"
                              size="sm"
                              color="blue"
                              onClick={() => {
                                queue.resetRetries(item.submissionId);
                                setItems(queue.getPending());
                                onManualSync?.();
                              }}
                            >
                              <IconRefresh size={14} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label="Aus der Warteschlange entfernen (Entwurf bleibt im Bericht)">
                            <ActionIcon
                              variant="subtle"
                              size="sm"
                              color="red"
                              onClick={() => {
                                queue.remove(item.submissionId);
                                setItems(queue.getPending());
                              }}
                            >
                              <IconTrash size={14} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Group>
                      <Divider mt="xs" />
                    </div>
                  );
                })}
              </Stack>
            </ScrollArea.Autosize>
          )}

          {items.length > 0 && (
            <Group justify="flex-end">
              <Button
                size="xs"
                leftSection={<IconRefresh size={14} />}
                onClick={retryAll}
              >
                Alle erneut versuchen
              </Button>
            </Group>
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
